import React, { useState, useEffect } from 'react';

const AITutorPersona: React.FC = () => {
  const [currentTrait, setCurrentTrait] = useState(0);
  const [animationPhase, setAnimationPhase] = useState('idle');
  const [eyePosition, setEyePosition] = useState({ x: 0, y: 0 });
  const [pulseIntensity, setPulseIntensity] = useState(0.5);

  const traits = [
    {
      title: "Adaptive Learning",
      description: "I adjust my teaching style to match your unique learning preferences",
      icon: "🧠",
      color: "#ff6b9d"
    },
    {
      title: "24/7 Availability",
      description: "Ready to help whenever you need assistance, day or night",
      icon: "⚡",
      color: "#4ecdc4"
    },
    {
      title: "Personalized Insights",
      description: "I analyze your progress to provide targeted study recommendations",
      icon: "📊",
      color: "#45b7d1"
    },
    {
      title: "Gamified Learning",
      description: "Making education fun with interactive challenges and rewards",
      icon: "🎮",
      color: "#96ceb4"
    },
    {
      title: "Emotional Support",
      description: "I understand when you're struggling and provide encouragement",
      icon: "💝",
      color: "#feca57"
    }
  ];

  const capabilities = [
    "🔍 Instant problem solving",
    "📚 Concept explanation",
    "🎯 Custom study plans",
    "🏆 Achievement tracking",
    "💡 Creative learning methods",
    "🤝 Study buddy simulation"
  ];

  useEffect(() => {
    // Trait cycling
    const traitTimer = setInterval(() => {
      setCurrentTrait(prev => (prev + 1) % 5); // Fixed number since traits array is constant
    }, 3000);

    // Animation phase cycling
    const animationTimer = setInterval(() => {
      const phases = ['idle', 'thinking', 'excited', 'focused'];
      setAnimationPhase(phases[Math.floor(Math.random() * phases.length)]);
    }, 2000);

    // Eye movement
    const eyeTimer = setInterval(() => {
      setEyePosition({
        x: (Math.random() - 0.5) * 20,
        y: (Math.random() - 0.5) * 10
      });
    }, 1500);

    // Pulse intensity
    const pulseTimer = setInterval(() => {
      setPulseIntensity(0.3 + Math.random() * 0.4);
    }, 800);

    return () => {
      clearInterval(traitTimer);
      clearInterval(animationTimer);
      clearInterval(eyeTimer);
      clearInterval(pulseTimer);
    };
  }, []);

  const getAvatarStyle = () => {
    const baseScale = animationPhase === 'excited' ? 1.1 : 
                    animationPhase === 'thinking' ? 0.95 : 1;
    
    return {
      transform: `scale(${baseScale}) ${animationPhase === 'thinking' ? 'rotate(-2deg)' : ''}`,
      filter: `hue-rotate(${currentTrait * 72}deg) brightness(${0.8 + pulseIntensity * 0.4})`,
      transition: 'all 0.5s ease'
    };
  };

  return (
    <div className="ai-tutor-persona" style={{
      padding: '2rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '20px',
      textAlign: 'center' as const,
      color: 'white',
      position: 'relative' as const,
      overflow: 'hidden'
    }}>
      <style>{`
        .ai-tutor-persona .persona-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 50% 50%, 
            rgba(255, 255, 255, 0.1) 0%, 
            transparent 70%);
          animation: backgroundPulse 4s ease-in-out infinite;
        }

        @keyframes backgroundPulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.7; }
        }

        .ai-tutor-persona .avatar-container {
          position: relative;
          display: inline-block;
          margin-bottom: 1.5rem;
        }

        .ai-tutor-persona .avatar {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background: linear-gradient(45deg, #ff6b9d, #4ecdc4, #45b7d1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          position: relative;
          box-shadow: 0 0 30px rgba(255, 255, 255, 0.3);
          animation: float 3s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        .ai-tutor-persona .avatar::before {
          content: '';
          position: absolute;
          top: -5px;
          left: -5px;
          right: -5px;
          bottom: -5px;
          border-radius: 50%;
          background: linear-gradient(45deg, #ff6b9d, #4ecdc4, #45b7d1);
          opacity: 0.5;
          animation: rotate 6s linear infinite;
          z-index: -1;
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .ai-tutor-persona .eyes {
          position: absolute;
          top: 40%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          gap: 8px;
        }

        .ai-tutor-persona .eye {
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          position: relative;
        }

        .ai-tutor-persona .pupil {
          width: 4px;
          height: 4px;
          background: #333;
          border-radius: 50%;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          transition: all 0.3s ease;
        }

        .ai-tutor-persona .name {
          font-size: 1.8rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        }

        .ai-tutor-persona .tagline {
          font-size: 1rem;
          opacity: 0.9;
          margin-bottom: 2rem;
          font-style: italic;
        }

        .ai-tutor-persona .trait-display {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 15px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
        }

        .ai-tutor-persona .trait-icon {
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
          animation: bounce 2s infinite;
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }

        .ai-tutor-persona .trait-title {
          font-size: 1.3rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
        }

        .ai-tutor-persona .trait-description {
          font-size: 0.95rem;
          opacity: 0.9;
          line-height: 1.4;
        }

        .ai-tutor-persona .capabilities-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-top: 1.5rem;
        }

        .ai-tutor-persona .capability-item {
          background: rgba(255, 255, 255, 0.1);
          padding: 0.8rem;
          border-radius: 10px;
          font-size: 0.9rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
        }

        .ai-tutor-persona .capability-item:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }

        .ai-tutor-persona .status-indicator {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: #00ff88;
          color: #000;
          padding: 0.3rem 0.8rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: bold;
          animation: statusPulse 2s infinite;
        }

        @keyframes statusPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .ai-tutor-persona {
            padding: 1.5rem !important;
          }
          
          .ai-tutor-persona .avatar {
            width: 100px !important;
            height: 100px !important;
            font-size: 2.5rem !important;
          }
          
          .ai-tutor-persona .capabilities-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div className="persona-background"></div>
      
      <div className="status-indicator">
        🟢 ACTIVE
      </div>

      <div className="avatar-container">
        <div className="avatar" style={getAvatarStyle()}>
          🤖
          <div className="eyes">
            <div className="eye">
              <div 
                className="pupil" 
                style={{
                  transform: `translate(calc(-50% + ${eyePosition.x}px), calc(-50% + ${eyePosition.y}px))`
                }}
              ></div>
            </div>
            <div className="eye">
              <div 
                className="pupil" 
                style={{
                  transform: `translate(calc(-50% + ${eyePosition.x}px), calc(-50% + ${eyePosition.y}px))`
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div className="name">WiZiAi Tutor</div>
      <div className="tagline">Your Personal Learning Companion</div>

      <div className="trait-display" style={{ borderColor: traits[currentTrait].color }}>
        <div className="trait-icon" style={{ color: traits[currentTrait].color }}>
          {traits[currentTrait].icon}
        </div>
        <div className="trait-title" style={{ color: traits[currentTrait].color }}>
          {traits[currentTrait].title}
        </div>
        <div className="trait-description">
          {traits[currentTrait].description}
        </div>
      </div>

      <div>
        <h4 style={{ marginBottom: '1rem', color: '#fff' }}>What I Can Do For You:</h4>
        <div className="capabilities-grid">
          {capabilities.map((capability, index) => (
            <div 
              key={index} 
              className="capability-item"
              style={{ 
                animationDelay: `${index * 0.1}s`,
                animation: 'slideInUp 0.6s ease forwards'
              }}
            >
              {capability}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AITutorPersona;